import type { RegionBorders, Zone, ZoneComponent, ZoneStyle } from '@tsagko/label-contracts'
import { projectPlacement, splitChildBoxes } from '@tsagko/label-zpl-pipeline'

export interface LaidOutZone {
  id: string
  x: number
  y: number
  w: number
  h: number
  component: ZoneComponent
  path: string[]
  /** Carried verbatim from the source Zone so ZoneRenderer can paint the
   * background rect (Task 13 of the block-variables-and-region-table slice).
   * Optional — only leaf zones with an explicit style have this set. */
  style?: ZoneStyle
  /** Region borders carried verbatim from the source Zone. Consumed by
   *  the canvas border layer. */
  borders?: RegionBorders
  /**
   * 0 for zones at the template level (interactive); ≥1 for zones nested
   * inside a baked-block-instance's expansion (presentation-only).
   *
   * ZoneRenderer reads this to suppress interactive chrome — dashed border,
   * hover effects, click handlers, split buttons — for interior leaves of a
   * dropped block, while still painting their backgrounds. Clicks fall
   * through to the underlying baked-instance host zone (which carries
   * `bakedDepth === 0`).
   */
  bakedDepth: number
}

export interface TemplateDims {
  widthMm: number
  heightMm: number
}

/**
 * Maximum nesting depth for baked-block-instance recursion. Mirrors the
 * pipeline's `MAX_BAKED_BLOCK_DEPTH` so designer and ZPL layouts agree on
 * cycle bailout semantics. Counts ONLY baked-block expansions; split
 * recursion is not counted because splits can't cycle by construction.
 */
const MAX_BAKED_BLOCK_DEPTH = 32

export function computeZoneLayout(root: Zone, dims: TemplateDims): LaidOutZone[] {
  const out: LaidOutZone[] = []

  function recurse(
    zone: Zone,
    x: number,
    y: number,
    w: number,
    h: number,
    path: string[],
    bakedDepth: number,
  ): void {
    if (zone.component !== undefined && zone.split === undefined) {
      out.push({
        id: zone.id,
        x,
        y,
        w,
        h,
        component: zone.component,
        path,
        style: zone.style,
        borders: zone.borders,
        bakedDepth,
      })
      // For a baked-block-instance leaf, recurse into the expansion at the
      // placement-projected box so the canvas can paint the block's interior
      // visual presentation (eg. zone-level backgrounds) at the right
      // position. Interior leaves carry `bakedDepth + 1` so ZoneRenderer
      // knows to skip their interactive chrome.
      if (zone.component.type === 'baked-block-instance') {
        const nextDepth = bakedDepth + 1
        if (nextDepth > MAX_BAKED_BLOCK_DEPTH) {
          throw new Error(
            `baked-block expansion exceeded max depth ${MAX_BAKED_BLOCK_DEPTH} — possible cycle at instance ${zone.component.id} (path: ${[...path, zone.component.id].join(' > ')})`,
          )
        }
        const projected = projectPlacement(
          { x, y, w, h },
          zone.component.placement,
        )
        recurse(
          zone.component.expansion,
          projected.x,
          projected.y,
          projected.w,
          projected.h,
          [...path, zone.component.id],
          nextDepth,
        )
      }
      return
    }
    if (zone.split !== undefined && zone.children !== undefined) {
      const [a, b] = zone.children
      const [boxA, boxB] = splitChildBoxes({ x, y, w, h }, zone.split)
      recurse(a, boxA.x, boxA.y, boxA.w, boxA.h, [...path, '0'], bakedDepth)
      recurse(b, boxB.x, boxB.y, boxB.w, boxB.h, [...path, '1'], bakedDepth)
      return
    }
    // Empty leaf: no component and no split. Emit a zone so the canvas can
    // render a "Drop a block here" hint and selection chrome. The component
    // field is left undefined; ZoneRenderer handles this case explicitly.
    out.push({
      id: zone.id,
      x,
      y,
      w,
      h,
      component: undefined as unknown as LaidOutZone['component'],
      path,
      style: zone.style,
      borders: zone.borders,
      bakedDepth,
    })
  }

  recurse(root, 0, 0, dims.widthMm, dims.heightMm, [], 0)
  return out
}
